import { useEffect, useRef } from 'react'

import { sequenceTracks } from '@hypersonic/shared/sequence'

import { get } from '~/api/fetch-client'
import type { Track } from '~/api/types'

import { NO_SEED, resolveRadioSeeds } from '~/components/player/radio-seed'
import type { PlayerState, QueueItem } from '~/components/player/types'
import { trackToQueueItem } from '~/components/player/utils'
import { useAppStore } from '~/hooks/use-app-store'

const RADIO_THRESHOLD = 2
const FETCH_LIMIT = 10
const MIN_RESULTS = 5

interface UseRadioModeOptions {
  state: PlayerState
  addToQueue: (tracks: QueueItem[]) => void
  pendingAdvanceRef: { current: boolean }
  advanceOne: () => void
}

export function useRadioMode({
  state,
  addToQueue,
  pendingAdvanceRef,
  advanceOne,
}: UseRadioModeOptions) {
  const { queue, currentIndex, radioMode } = state
  const fetchingRef = useRef(false)
  const lastFetchedTrackIdRef = useRef<number | null>(null)
  const prevRadioModeRef = useRef(radioMode)
  const radioSettings = useAppStore((s) => s.radioSettings)
  const radioFeedback = useAppStore((s) => s.radioFeedback)

  useEffect(() => {
    if (radioMode && !prevRadioModeRef.current) {
      lastFetchedTrackIdRef.current = null
    }
    // Leaving radio mode abandons any advance radio still owed, so drop the flag
    // rather than letting it fire against a later, unrelated fetch.
    if (!radioMode) pendingAdvanceRef.current = false
    prevRadioModeRef.current = radioMode
  }, [radioMode, pendingAdvanceRef])

  useEffect(() => {
    if (!radioMode) return
    if (fetchingRef.current) return

    const { seedId, moodSeedId } = resolveRadioSeeds(queue, currentIndex)

    // Playback stalls here waiting on radio, so every path out of this effect has
    // to either queue something or surface the dead end. Returning silently would
    // leave the player stopped with no way for the user to recover.
    if (seedId === NO_SEED) {
      if (pendingAdvanceRef.current) {
        pendingAdvanceRef.current = false
        useAppStore.getState().setRadioDeadEnd(NO_SEED)
      }
      return
    }

    // Only count radio-generated tracks as the lookahead buffer — album tracks
    // queued manually don't prevent radio from seeding off the current track.
    const radioTracksAhead = queue.slice(currentIndex + 1).filter((t) => t.radioGenerated).length
    if (radioTracksAhead > RADIO_THRESHOLD) return

    // Skip re-fetch unless the user triggered a manual next (pendingAdvanceRef)
    if (!pendingAdvanceRef.current && lastFetchedTrackIdRef.current === seedId) return

    fetchingRef.current = true
    lastFetchedTrackIdRef.current = seedId

    // Interstitial ids come from a different table and collide with track ids, so
    // including them here would filter out unrelated legitimate candidates.
    const existingIds = new Set(queue.filter((t) => !t.isInterstitial).map((t) => t.id))

    const fetchAndEnqueue = async () => {
      let queued = false

      try {
        let candidates: Track[] = []

        try {
          const { moodTargets, embeddingWeight } = radioSettings
          const targetParams = Object.fromEntries(
            Object.entries(moodTargets).filter(([, v]) => v !== null)
          ) as Record<string, number>
          // Don't steer toward/away from tracks already in the queue (the seed
          // and recent context), only from genuine out-of-queue taste signals.
          const likeIds = radioFeedback.likeIds.filter((tid) => !existingIds.has(tid))
          const avoidIds = radioFeedback.dislikeIds
          const feedbackParams: Record<string, string> = {}
          if (likeIds.length > 0) feedbackParams.likeIds = likeIds.join(',')
          if (avoidIds.length > 0) feedbackParams.avoidIds = avoidIds.join(',')
          const moodRes = await get<{ data: Track[] }>(`/tracks/${moodSeedId}/mood-similar`, {
            limit: FETCH_LIMIT,
            embeddingWeight,
            ...targetParams,
            ...feedbackParams,
          })
          candidates = moodRes.data.filter((t) => !existingIds.has(t.id))
        } catch (err) {
          console.warn('[Radio] mood-similar unavailable, falling back to Last.fm:', err)
        }

        if (candidates.length < MIN_RESULTS) {
          try {
            const lastFmRes = await get<{ data: Track[] }>(`/tracks/${seedId}/similar`, {
              limit: FETCH_LIMIT,
            })
            const lastFmCandidates = lastFmRes.data.filter((t) => !existingIds.has(t.id))
            const seen = new Set(candidates.map((t) => t.id))
            for (const t of lastFmCandidates) {
              if (!seen.has(t.id)) {
                seen.add(t.id)
                candidates.push(t)
              }
            }
          } catch (err) {
            console.warn('[Radio] Last.fm fallback unavailable:', err)
          }
        }

        // Radio can be switched off mid-fetch — an explicit play() does exactly
        // that — and these tracks must not land in the queue that replaced ours.
        if (!useAppStore.getState().playerSettings.radioMode) {
          pendingAdvanceRef.current = false
          return
        }

        if (candidates.length > 0) {
          const weights = {
            harmonic: radioSettings.harmonicWeight,
            bpm: radioSettings.bpmWeight,
            artist: radioSettings.artistWeight,
          }
          // Interstitials carry no artist, and their ids belong to another table.
          const history = queue.filter((t) => !t.isInterstitial)
          const sequenced = sequenceTracks({ tracks: candidates, weights, history })
          addToQueue(sequenced.map((t) => ({ ...trackToQueueItem(t), radioGenerated: true })))
          queued = true
        } else {
          console.warn('[Radio] No candidates found for seed track', seedId)
        }
      } catch (err) {
        console.error('[Radio] Fetch failed:', err)
      } finally {
        fetchingRef.current = false

        // Resolved last, so it runs whether the fetch queued tracks, came back
        // empty, or threw. A proactive top-up that misses isn't a freeze — only
        // an advance playback is actually waiting on becomes a dead end.
        if (pendingAdvanceRef.current) {
          pendingAdvanceRef.current = false
          if (queued) {
            try {
              advanceOne()
            } catch (err) {
              console.error('[Radio] Advance failed:', err)
              queued = false
            }
          }
          if (!queued) useAppStore.getState().setRadioDeadEnd(seedId)
        }
      }
    }

    fetchAndEnqueue()
  }, [
    currentIndex,
    queue.length,
    radioMode,
    queue,
    addToQueue,
    pendingAdvanceRef,
    advanceOne,
    radioSettings,
    radioFeedback,
  ])
}
